import { emailSchema } from '@retainerclub/common';
import * as Yup from 'yup';

const PASSWORD_REGEX =
  /^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/;
const YEAR_REGEX = /(19|20)\d{2}/;
const MONTH_REGEX = /(01|02|03|04|05|06|07|08|09|10|11|12)/;
const DAY_REGEX =
  /(01|02|03|04|05|06|07|08|09|10|11|12|13|14|15|16|17|18|19|20|21|22|23|24|25|26|27|28|29|30|31)/;

// Each plan or subscription on the account must be agreed to.
function acknowledgmentSchema() {
  return Yup.object().shape({
    key: Yup.string().required(),
    agreed: Yup.boolean().oneOf(
      [true],
      'You must agree to the subscription terms to continue',
    ),
    selectedSubscriptionProductId: Yup.string().nullable(true),
    lowerTiersExpanded: Yup.boolean().nullable(true),
  });
}

function schema({ referral }) {
  // Payment fields are required for a free-trial referral, and whenever the
  // account's pending plans charge soon after the first shipment.
  const isFreeTrial =
    referral?.isFreeTrial === true ||
    referral?.subscriptionDisclosure?.requiresPaymentMethod === true;
  const disclosureRequired =
    referral?.subscriptionDisclosure?.required === true;

  return Yup.object().shape({
    id: Yup.string().nullable(true),
    consentSelectAll: Yup.boolean().nullable(true),
    subscriptionAcknowledgments: disclosureRequired
      ? Yup.array().of(acknowledgmentSchema()).min(1)
      : Yup.array().nullable(true),
    partnerLocationId: Yup.string().nullable(true),
    userAttributes: Yup.object().shape({
      firstName: Yup.string().required('first name is required'),
      lastName: Yup.string().required('last name is required'),
      email: emailSchema({ required: true }),
      phone: Yup.string()
        .required('mobile number is required')
        .test('phone-format', 'mobile number must be valid', (value) => {
          if (!value) {
            return false;
          }
          // Remove all non-digit characters to check the total length
          const digitsOnly = value.replace(/\D/g, '');
          // Should be at least 11 digits (country code + 10 digit number)
          // This accommodates different country code lengths
          return digitsOnly.length >= 11;
        }),
      password: Yup.string()
        .required('Password is required')
        .matches(
          PASSWORD_REGEX,
          'Password must contain at least 8 characters, one uppercase, one number and one special case character',
        ),
      acceptTerms: Yup.boolean()
        .required('You must accept the terms and conditions')
        .oneOf([true], 'The terms and conditions must be accepted.'),
      acceptSms: Yup.boolean().required('You must accept the SMS terms'),
      acceptMarketing: Yup.boolean().required(
        'You must accept the marketing terms',
      ),
    }),
    accountAddressesAttributes: Yup.array().of(
      Yup.object().shape({
        address: Yup.string().required('address is required'),
        address2: Yup.string().nullable(true),
        city: Yup.string().required('city is required'),
        region: Yup.string().required('region / state is required'),
        postalCode: Yup.string().required('postal code is required'),
        country: Yup.string().required('country is required'),
        isShipping: Yup.boolean().required('is shipping is required'),
        isDefault: Yup.boolean().required('is default is required'),
      }),
    ),
    creditCard: isFreeTrial
      ? Yup.object().shape({
          nameOnCard: Yup.string().required('name on card is required'),
          cardNumber: Yup.string().required('card number is required'),
          expirationDate: Yup.string().required('expiration date is required'),
          cvv: Yup.string().required('cvv is required'),
        })
      : Yup.object().shape({
          nameOnCard: Yup.string().nullable(true),
          cardNumber: Yup.string().nullable(true),
          expirationDate: Yup.string().nullable(true),
          cvv: Yup.string().nullable(true),
        }),
    billingAddress: isFreeTrial
      ? Yup.object().shape({
          address: Yup.string().required('address is required'),
          address2: Yup.string().nullable(true),
          city: Yup.string().required('city is required'),
          region: Yup.string().required('region / state is required'),
          postalCode: Yup.string().required('postal code is required'),
          country: Yup.string().required('country is required'),
        })
      : Yup.object().shape({
          address: Yup.string().nullable(true),
          address2: Yup.string().nullable(true),
          city: Yup.string().nullable(true),
          region: Yup.string().nullable(true),
          postalCode: Yup.string().nullable(true),
          country: Yup.string().nullable(true),
        }),
    patientAttributes: Yup.object({
      firstName: Yup.string().required('patient first name is required'),
      lastName: Yup.string().required('patient last name is required'),
      birthDay: Yup.string()
        .required('day of birth is required')
        .matches(DAY_REGEX, 'day must be between 01 and 31'),
      birthMonth: Yup.string()
        .required('birth month is required')
        .matches(MONTH_REGEX, 'month must be between 01 and 12'),
      birthYear: Yup.string()
        .required('birth year is required')
        .matches(YEAR_REGEX, 'year must be between 1900 and 2099'),
    }),
  });
}

export default schema;
